<template>
  <div
    :id="elementId"
    ref="containerRef"
    :class="{ reduced, fullwidth: fullWidth }"
  >
    <FederatedSearchLoadingPlaceholder
      v-if="!isInitialized"
      :class="{ fullwidth: fullWidth }"
      :with-test-ids="true"
      @focusin="initAutocomplete"
      @pointerdown="initAutocomplete"
    />
  </div>
</template>

<script setup lang="ts">
import { nextTick, onUnmounted, ref, watch, render as vueRender } from 'vue'
import { useI18n } from 'vue-i18n'
import { onClickOutside } from '@vueuse/core'
import FederatedSearchLoadingPlaceholder from './FederatedSearchLoadingPlaceholder.vue'
import { sendSearch, sendViewItemList } from '~/composables/useGtmEvents'
import { useAlgoliaSearchClient } from '~/composables/algolia/useAlgoliaSearchClient'
import { useAlgoliaSearchInsights } from '~/composables/algolia/useAlgoliaSearchInsights'
import { useLastViewedEvents } from '~/composables/useLastViewedEvents'
import { sanitizeAlgoliaQueryId } from '~/utils/helpers'
import {
  getProductPath,
  HITS_PER_PAGE,
  mountUrlParamsFromItem,
} from './helpers'
import {
  getFederatedSearchItemListIds,
  getVisibleEventItems,
} from './gtmItemLists'
import { useFederatedSearchAnalyticsSession } from '~/composables/algolia/useFederatedSearchAnalyticsSession'
import { useFederatedSearchSources } from './useFederatedSearchSources'
import { createPanelLayout } from './renderPanel'
import {
  useRouter,
  useRoute,
  useRuntimeConfig,
  useLocalePath,
  navigateTo,
} from '#imports'

const {
  elementId,
  reduced = false,
  fullWidth = false,
  analyticsPlacement,
} = defineProps<{
  elementId: string
  reduced?: boolean
  fullWidth?: boolean
  analyticsPlacement?: 'homepage' | 'navbar'
}>()

const { t, locale } = useI18n()
const { isMobile } = useDevice()
const router = useRouter()
const localePath = useLocalePath()
const config = useRuntimeConfig()
// Reuse the shared `$algolia` client instead of building a second one — see KFT-3678.
const searchClient = useAlgoliaSearchClient()
const { client: algoliaInsightsClient, token: userToken } =
  useAlgoliaSearchInsights()
const algoliaAutocompleteInstance = ref<any>(null)
const isInitialized = ref(false)
const { lastViewedEvents, loadLastViewedEvents } = useLastViewedEvents(false)
const hasLoadedLastViewedEvents = ref(false)
const isLastViewedEventsReady = ref(false)
const eventsQueryId = ref<string | null>(null)
const eventsNbHits = ref<number | null>(null)
const isAutocompleteOpen = ref(false)
const containerRef = ref<HTMLElement | null>(null)
const { setFederatedSearchAnalyticsOutcome } =
  useFederatedSearchAnalyticsSession()

const getSources = useFederatedSearchSources({
  searchClient,
  locale,
  isMobile,
  localePath,
  t,
  router,
  eventsQueryId,
  eventsNbHits,
  appEnv: config.public.appEnv,
  analyticsPlacement,
})

// GA4 `view_item_list` for the panel lists (not per keystroke):
// - event results: once per settled query (debounced like the `search` event
//   below), and only while the events column is visible — with an empty query
//   it is hidden in favour of the last viewed column (see renderPanel.ts).
// - last viewed: once per panel open, when that column is shown.
// Both reset when the panel closes.
const federatedSearchItemListIds =
  getFederatedSearchItemListIds(analyticsPlacement)
const autocompleteQuery = ref('')
let lastResultsViewQuery: string | null = null
let pendingResultsViewQuery: string | null = null
let resultsViewTimer: ReturnType<typeof setTimeout> | null = null
let hasSentLastViewedView = false

const cancelPendingResultsView = () => {
  if (resultsViewTimer) {
    clearTimeout(resultsViewTimer)
    resultsViewTimer = null
  }
  pendingResultsViewQuery = null
}

// Mirrors renderPanel.ts: with an empty query the events column is hidden when
// there are last viewed events. Those load async on first open, so an empty
// query only counts as visible once loading finished and returned nothing.
const isEventsColumnVisible = (rawQuery: string) =>
  Boolean(rawQuery) ||
  (isLastViewedEventsReady.value && !lastViewedEvents.value?.length)

let lastOpenAutocompleteState: any = null

const trackAutocompleteResultsView = (state: any) => {
  if (!state.isOpen) {
    cancelPendingResultsView()
    lastResultsViewQuery = null
    lastOpenAutocompleteState = null
    return
  }
  lastOpenAutocompleteState = state
  if (state.status !== 'idle') return
  const rawQuery = state.query ?? ''
  const query = rawQuery.trim()
  const items = getVisibleEventItems(state)
  if (!isEventsColumnVisible(rawQuery) || !items.length) {
    if (pendingResultsViewQuery === query) cancelPendingResultsView()
    return
  }
  // Hovering items re-fires onStateChange with the same query — don't restart
  // a timer that is already pending for it.
  if (query === lastResultsViewQuery || query === pendingResultsViewQuery) {
    return
  }
  cancelPendingResultsView()
  pendingResultsViewQuery = query
  resultsViewTimer = setTimeout(() => {
    resultsViewTimer = null
    pendingResultsViewQuery = null
    // Re-check: last viewed may have loaded and hidden the column meanwhile
    if (!isEventsColumnVisible(rawQuery)) return
    lastResultsViewQuery = query
    sendViewItemList(federatedSearchItemListIds.results, items)
  }, 1200)
}

// Last viewed finishing loading changes empty-query visibility without a new
// autocomplete state change, so re-evaluate against the latest open state.
watch([isLastViewedEventsReady, lastViewedEvents], () => {
  if (lastOpenAutocompleteState) {
    trackAutocompleteResultsView(lastOpenAutocompleteState)
  }
})

watch([isAutocompleteOpen, lastViewedEvents, autocompleteQuery], () => {
  if (!isAutocompleteOpen.value) {
    hasSentLastViewedView = false
    return
  }
  if (hasSentLastViewedView || autocompleteQuery.value) return
  const events = lastViewedEvents.value?.slice(0, HITS_PER_PAGE.lastViewed)
  if (!events?.length) return
  hasSentLastViewedView = true
  sendViewItemList(federatedSearchItemListIds.lastViewed, events)
})

let isNavigatingWithKeyboard = false
let removeKeyboardListeners: (() => void) | null = null

// GA4 `search` event for the autocomplete (instant search). Debounced (1200ms)
// so we record the query the user paused on rather than every keystroke. The
// pending timer is cancelled on any navigation (see the route watcher below):
// pressing Enter or selecting a result goes to `/search`, which emits its own
// `search` event, so cancelling avoids a double-count for the common
// type→Enter flow. `count` is the total matching events (Algolia `nbHits`, the
// same figure the /search page shows), not the capped rows in the panel; 0 is
// a zero-result search.
let lastSentSearchTerm: string | null = null
let searchEventTimer: ReturnType<typeof setTimeout> | null = null

const cancelPendingSearchEvent = () => {
  if (searchEventTimer) {
    clearTimeout(searchEventTimer)
    searchEventTimer = null
  }
}

const scheduleAutocompleteSearchEvent = (query: string, count: number) => {
  cancelPendingSearchEvent()
  searchEventTimer = setTimeout(() => {
    searchEventTimer = null
    const term = query.trim()
    if (!term || term === lastSentSearchTerm) return
    lastSentSearchTerm = term
    sendSearch(term, count)
  }, 1200)
}

// Lazy-init: @algolia/autocomplete-js is dynamic-imported and the autocomplete
// instance is only created on first interaction with the placeholder input, so
// the bundle and its document listeners stay off pages until the box is used.
const initAutocomplete = async () => {
  if (isInitialized.value) return
  isInitialized.value = true

  const { autocomplete } = await import('@algolia/autocomplete-js')

  // Wait for the placeholder to be removed so autocomplete mounts into an empty
  // container.
  await nextTick()

  const el = document.getElementById(elementId)
  if (!el) return

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      isNavigatingWithKeyboard = true
    }
  }

  const handleMouseDown = () => {
    isNavigatingWithKeyboard = false
  }

  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('mousedown', handleMouseDown)
  removeKeyboardListeners = () => {
    document.removeEventListener('keydown', handleKeyDown)
    document.removeEventListener('mousedown', handleMouseDown)
  }

  algoliaAutocompleteInstance.value = autocomplete({
    // Deterministic, instance-unique element ids (e.g. `#banner-autocomplete-input`)
    // instead of a shared `autocomplete-N` counter that shifts with init order.
    id: elementId,
    container: `#${elementId}`,
    defaultActiveItemId: null,
    onStateChange({ state }) {
      isAutocompleteOpen.value = state.isOpen
      autocompleteQuery.value = state.query ?? ''
      trackAutocompleteResultsView(state)

      if (state.status === 'idle' && state.query?.trim()) {
        scheduleAutocompleteSearchEvent(state.query, eventsNbHits.value ?? 0)
      }

      if (state.isOpen && !hasLoadedLastViewedEvents.value) {
        hasLoadedLastViewedEvents.value = true
        loadLastViewedEvents().finally(() => {
          isLastViewedEventsReady.value = true
        })
      }

      if (
        isNavigatingWithKeyboard &&
        state.activeItemId === null &&
        state.collections.some((col) => col.items.length > 0)
      ) {
        algoliaAutocompleteInstance.value.setActiveItemId(0)
      }
    },
    openOnFocus: true,
    insights: {
      insightsClient: algoliaInsightsClient,
      onItemsChange({ insights, insightsEvents }) {
        const events = insightsEvents.map((insightsEvent) => ({
          ...insightsEvent,
          userToken: userToken.value,
        }))
        insights.viewedObjectIDs(...events)
      },
    },
    translations: {
      detachedCancelButtonText: t('search.buttons.cancel'),
    },
    renderer: { createElement },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render({ elements, render, state, setIsOpen }, root) {
      const panelLayout = createPanelLayout({
        elements,
        state,
        setIsOpen,
        lastViewedEvents,
        lastViewedItemListId: federatedSearchItemListIds.lastViewed,
        localePath,
        t,
      })
      vueRender(panelLayout, root)

      if (fullWidth) {
        const resultsPanel = document.getElementsByClassName('aa-Panel')
        if (resultsPanel && resultsPanel[0]) {
          resultsPanel[0].classList.add('aa-Panel-fullwidth')
        }
      }
    },
    getSources,
    placeholder: t('search.algoliaAutocomplete.placeholder'),
    onSubmit({ state, setIsOpen }: any) {
      setIsOpen(false)

      if (state?.context?.redirects?.url) {
        navigateTo(state?.context.redirects?.url, { external: true })
        return
      }

      setFederatedSearchAnalyticsOutcome('search_results')

      const updatedQuery = {
        ...(state?.query
          ? { searchquery: state?.query, resetFilters: 'true' }
          : {}),
      }

      navigateTo({
        path: localePath('/search/'),
        query: updatedQuery,
      })
    },
    navigator: {
      navigate({ state }) {
        const activeItem = state?.collections
          ?.flatMap((collection) => collection.items)
          ?.at(state?.activeItemId ?? -1)

        if (isNavigatingWithKeyboard && activeItem) {
          if (activeItem.__autocomplete_indexName.includes('categories')) {
            setFederatedSearchAnalyticsOutcome('category_results')
            navigateTo(localePath(`/c/${activeItem?.slug}`))
            return
          }

          if (activeItem.__autocomplete_indexName.includes('locations')) {
            setFederatedSearchAnalyticsOutcome('location_results')
            navigateTo(
              localePath(`/search/?${mountUrlParamsFromItem(activeItem)}`)
            )
            return
          }

          if (
            activeItem.__autocomplete_indexName.includes('events') ||
            activeItem.__autocomplete_indexName.includes('products')
          ) {
            const path = localePath(getProductPath(activeItem))
            const qid = sanitizeAlgoliaQueryId(eventsQueryId.value)
            window.location.href = qid ? `${path}?qid=${qid}` : path
            return
          }
        }

        if (state?.context?.redirects?.url) {
          navigateTo(state.context.redirects.url, { external: true })
          return
        }

        setFederatedSearchAnalyticsOutcome('search_results')
        navigateTo({
          path: localePath('/search/'),
          query: { searchquery: state?.query },
        })
      },
    },
  })

  // Forward the user's interaction to the freshly-mounted autocomplete: focus
  // the inline input, or open the detached overlay on touch/mobile where no
  // inline input exists.
  await nextTick()
  const inlineInput = el.querySelector<HTMLInputElement>('.aa-Input')
  if (inlineInput) {
    inlineInput.focus()
  } else {
    algoliaAutocompleteInstance.value?.setIsOpen?.(true)
    await nextTick() // let the detached overlay mount
  }

  // Carry the placeholder's e2e hook onto the mounted input so the selector
  // survives lazy-init, scoped to this instance via its deterministic id.
  document
    .getElementById(`${elementId}-input`)
    ?.setAttribute('data-test-id', 'federated-search-input')
}

onUnmounted(() => {
  removeKeyboardListeners?.()
  cancelPendingSearchEvent()
  cancelPendingResultsView()
  algoliaAutocompleteInstance.value?.destroy?.()
})

if (import.meta.client && fullWidth) {
  onClickOutside(containerRef, (event: Event) => {
    const t = event.target as HTMLElement | null
    if (t?.closest?.('.aa-DetachedOverlay, .aa-DetachedContainer')) {
      return
    }
    if (isAutocompleteOpen.value && algoliaAutocompleteInstance.value) {
      algoliaAutocompleteInstance.value.setIsOpen(false)
    }
  })
}

const route = useRoute()
watch(
  () => route.fullPath,
  () => {
    // A navigation (Enter → /search, a suggestion select, keyboard nav) drops
    // the pending dropdown `search` event so it doesn't double-count with the
    // event the destination page emits.
    cancelPendingSearchEvent()
    setTimeout(() => {
      algoliaAutocompleteInstance.value?.setIsOpen(false)
    }, 0)
  }
)
</script>
